/**
 * 2Side World — premium motion layer.
 *
 * Progressive enhancement on top of Horizon's own interactivity: everything
 * here is purely visual (text/image reveals, magnetic buttons, card tilt).
 *
 * IMPORTANT — why there is no Lenis / ScrollTrigger here:
 * Horizon does not scroll the window on desktop. At >=990px `base.css` sets
 * `html, body { height: 100dvh; overflow: hidden }` and scrolls an inner
 * container (`.page-wrapper`) instead. Libraries that assume window scroll
 * (Lenis, GSAP ScrollTrigger) therefore attach to an element that never
 * scrolls: Lenis swallows wheel events and breaks scrolling outright, and
 * ScrollTrigger never fires, leaving reveal targets stuck hidden forever.
 *
 * So scroll-driven reveals use IntersectionObserver, which reports viewport
 * intersection regardless of *which* element does the scrolling. That works
 * under both Horizon layouts (window scroll on mobile, .page-wrapper scroll
 * on desktop), needs no scroller configuration, and ships less JavaScript.
 *
 * Everything is fail-open: elements are only hidden immediately before they
 * are observed, and any failure re-reveals them, so a CDN hiccup can never
 * leave the storefront with invisible content.
 *
 * Re-runs on `shopify:section:load` so sections edited/added in the Theme
 * Editor pick up the same animations without a full page reload.
 */
import gsap from 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/+esm';
import SplitType from 'https://cdn.jsdelivr.net/npm/split-type@0.3.4/+esm';

const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FINE_POINTER = window.matchMedia('(pointer: fine)').matches;

function whenReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

/**
 * Runs `onEnter(el)` once, when `el` first scrolls into view.
 * Uses IntersectionObserver so it is independent of the scroll container.
 */
function onceInView(el, onEnter) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        onEnter(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px' }
  );

  observer.observe(el);
}

/** Line-by-line text reveal. Add `data-reveal-text` to a heading, and
 *  `data-reveal-immediate` to play on load rather than on scroll. */
function revealText(root) {
  const els = root.querySelectorAll('[data-reveal-text]:not([data-reveal-done])');

  els.forEach((el) => {
    el.dataset.revealDone = 'true';

    let lines;
    try {
      const split = new SplitType(el, { types: 'lines', lineClass: 'reveal-line' });
      lines = split.lines;
      if (!lines || !lines.length) return;

      lines.forEach((line) => {
        const mask = document.createElement('span');
        mask.style.display = 'block';
        mask.style.overflow = 'hidden';
        line.parentNode.insertBefore(mask, line);
        mask.appendChild(line);
      });
    } catch (error) {
      return; // leave the text exactly as authored
    }

    const play = () =>
      gsap.to(lines, {
        yPercent: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.08,
      });

    gsap.set(lines, { yPercent: 110 });

    if (el.hasAttribute('data-reveal-immediate')) {
      gsap.delayedCall(0.2, play);
    } else {
      onceInView(el, play);
    }
  });
}

/** Simple fade + rise. Add `data-reveal` to any element. */
function revealFade(root) {
  const els = root.querySelectorAll('[data-reveal]:not([data-reveal-done])');

  els.forEach((el) => {
    el.dataset.revealDone = 'true';
    gsap.set(el, { autoAlpha: 0, y: 24 });

    onceInView(el, () =>
      gsap.to(el, { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power2.out' })
    );
  });
}

/** Product grids: cards rise in one after another the first time the grid
 *  scrolls into view. Items appended later (pagination) are left visible. */
function revealCards(root) {
  const grids = root.querySelectorAll(
    '.product-grid:not([data-reveal-done]), .shop-window__grid:not([data-reveal-done])'
  );

  grids.forEach((grid) => {
    grid.dataset.revealDone = 'true';
    const cards = grid.querySelectorAll(':scope > *');
    if (!cards.length) return;

    gsap.set(cards, { autoAlpha: 0, y: 32 });
    onceInView(grid, () =>
      gsap.to(cards, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 })
    );
  });
}

/** Slow, masked image reveal. Add `data-reveal-image` to the wrapper. */
function revealImages(root) {
  const els = root.querySelectorAll('[data-reveal-image]:not([data-reveal-done])');

  els.forEach((el) => {
    el.dataset.revealDone = 'true';
    const media = el.querySelector('img, svg, video') || el;

    gsap.set(el, { overflow: 'hidden' });
    gsap.set(media, { scale: 1.12 });

    onceInView(el, () =>
      gsap.to(media, { scale: 1, duration: 1.4, ease: 'power3.out' })
    );
  });
}

/** Slight 3D tilt on product cards, following the pointer. Runs on
 *  `.product-card` (the ancestor of the media), so it composes cleanly
 *  with the card's own hover-zoom transform instead of fighting it. */
function initProductTilt(root) {
  if (!FINE_POINTER) return;
  const els = root.querySelectorAll(
    '.product-card:not([data-tilt-done]), .shop-window__placeholder:not([data-tilt-done])'
  );

  els.forEach((el) => {
    el.dataset.tiltDone = 'true';
    const maxTilt = 6;

    gsap.set(el, { transformPerspective: 800 });
    const rotateX = gsap.quickTo(el, 'rotateX', { duration: 0.4, ease: 'power3.out' });
    const rotateY = gsap.quickTo(el, 'rotateY', { duration: 0.4, ease: 'power3.out' });

    el.addEventListener('mousemove', (event) => {
      const rect = el.getBoundingClientRect();
      const relX = (event.clientX - rect.left) / rect.width - 0.5;
      const relY = (event.clientY - rect.top) / rect.height - 0.5;
      rotateY(relX * maxTilt * 2);
      rotateX(relY * -maxTilt * 2);
    });

    el.addEventListener('mouseleave', () => {
      rotateX(0);
      rotateY(0);
    });
  });
}

/** Subtle magnetic pull on pointer-fine devices. Add `data-magnetic`. */
function initMagnetic(root) {
  if (!FINE_POINTER) return;
  const els = root.querySelectorAll('[data-magnetic]:not([data-magnetic-done])');

  els.forEach((el) => {
    el.dataset.magneticDone = 'true';
    const strength = 0.3;
    const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3.out' });

    el.addEventListener('mousemove', (event) => {
      const rect = el.getBoundingClientRect();
      xTo((event.clientX - rect.left - rect.width / 2) * strength);
      yTo((event.clientY - rect.top - rect.height / 2) * strength);
    });

    el.addEventListener('mouseleave', () => {
      xTo(0);
      yTo(0);
    });
  });
}

function initAll(root) {
  if (REDUCE_MOTION) return;

  // Each effect is isolated: one failing must never hide content or stop
  // the others from running.
  [revealText, revealFade, revealCards, revealImages, initProductTilt, initMagnetic].forEach((init) => {
    try {
      init(root);
    } catch (error) {
      console.warn('[2side-motion] skipped an effect:', error);
    }
  });
}

whenReady(() => initAll(document));

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
